import { useEffect, useState } from "react";
import {
  EditorState,
  convertToRaw,
  convertFromRaw,
  ContentState,
  Modifier,
  convertFromHTML,
} from "draft-js";
import { Editor } from "react-draft-wysiwyg";
import "react-draft-wysiwyg/dist/react-draft-wysiwyg.css";
import "./WysiwygEditor.scss";
import { isValidJson } from "./WysiwygHelpers";

const styleMap = {
  FONT_SIZE_12: {
    fontSize: "16px"
  }
};

const WysiwygEditor = (props) => {
  const createEditorInstance = (value) => {
    if (!value || value === "") {
      return EditorState.createEmpty();
    }

    if (isValidJson(value)) {
      const content = convertFromRaw(JSON.parse(value));

      return EditorState.createWithContent(content);
    } else {
      const content = ContentState.createFromText(value);

      return EditorState.createWithContent(content);
    }
  };

  const initState = createEditorInstance(props.value);

  const getRawContentLength = (editorState) => {
    const contentState = editorState.getCurrentContent();
    const plainText = contentState.getPlainText();

    return plainText.length;
  };

  const [state, setState] = useState({
    editorState: initState,
    counter: getRawContentLength(initState),
    isFocused: false,
  });

  useEffect(() => {
    if (state.isFocused) {
      return;
    }

    const currentValue = JSON.stringify(convertToRaw(state.editorState.getCurrentContent()));
    if (props.value && props.value !== currentValue) {
      const newEditorState = createEditorInstance(props.value);
      setState(prev => ({
        ...prev,
        editorState: newEditorState,
        counter: getRawContentLength(newEditorState),
      }));
    }
  }, [props.value]);

  const allowedBlockTypes = ["ordered-list-item", "unordered-list-item"];

  const removeFormatting = (contentState) => {
    const blockMap = contentState.getBlockMap();
    const newBlockMap = blockMap.map(block => {

      if (!allowedBlockTypes.includes(block.getType())) {
        block = block.set('type', 'unstyled');
      }
      
      const characterList = block.getCharacterList().map(character => {
        const newStyle = character.get('style').clear().add("FONT_SIZE_12");
        
        return character.set('style', newStyle);
      });
      return block.set('characterList', characterList);
    });
  
    return contentState.merge({
      blockMap: newBlockMap
    });
  };

  const handlePastedText = (text, html, editorState) => {
    let newContent = editorState.getCurrentContent();
    let selection = editorState.getSelection();

    if (html) {
      const blocksFromHTML = convertFromHTML(html);
      let contentState = ContentState.createFromBlockArray(
        blocksFromHTML.contentBlocks,
        blocksFromHTML.entityMap
      );

      contentState = removeFormatting(contentState);

      const targetSelection = editorState.getSelection();
      const blockMap = contentState.getBlockMap();
      const firstBlock = blockMap.first();

      // Check if first block is empty and remove it
      if (firstBlock.getText().trim() === "") {
        newContent = Modifier.removeRange(
          newContent,
          targetSelection,
          "backward"
        );
      }

      // Merge new content
      newContent = Modifier.replaceWithFragment(
        newContent,
        targetSelection,
        contentState.getBlockMap()
      );
    } else {
      newContent = Modifier.insertText(newContent, selection, text);
    }

    const newContentLength = getRawContentLength(
      EditorState.push(editorState, newContent, "insert-characters")
    );

    if (newContentLength > props.size) {
      console.log(`Pasted content exceeds limit: ${newContentLength}`);
      return "handled"; // Prevent the paste action
    }

    setState({
      ...state,
      counter: newContentLength,
      editorState: EditorState.push(
        editorState,
        newContent,
        "insert-characters"
      ),
    });
    return "handled";
  };

  const handleEditorStateChange = (editorState) => {

    const value = JSON.stringify(convertToRaw(editorState.getCurrentContent()));

    const contentLength = getRawContentLength(editorState);

    setState(prev => {
      if (prev.counter === contentLength && prev.editorState === editorState) return prev;
      return {
        ...prev,
        editorState,
        counter: contentLength,
      };
    });

    if (props.onChange) {
      if (contentLength === 0) {
        props.onChange({ target: { value: "", name: props.name } });
        return;
      }

      props.onChange({
        target: {
          value,
          name: props.name,
        },
      });
    }
  };

  const handleBeforeInput = () => {
    if (props.size) {
      if (state.counter >= props.size) {
        return true;
      }
      return false;
    }
  };

  const handleKeyCommand = (command, editorState) => {
    const contentLength = getRawContentLength(editorState);

    if (props.size && contentLength >= props.size) {
      if (command === 'split-block' || command === 'insert-characters') {
        return 'handled';
      }
    }

    return 'not-handled';
  };

  return (
    <section className={`wysiwygEditor${state.isFocused ? " focused" : ""}`}>
      {props.label ? <div className="label">{props.label}</div> : null}
      <Editor
        aria-describedby={props.errorId ?? null}
        aria-label={
          props.size
            ? `${props.label}, maksymalna liczba znaków: ${props.size}. Wpisano ${state.counter} znaków.`
            : undefined
        }
        toolbar={{
          options: ["list"],
          list: {
            inDropdown: false,
            options: ["ordered", "indent", "outdent"],
          },
        }}
        customStyleMap={styleMap}
        localization={{ locale: "pl" }}
        editorState={state.editorState}
        toolbarClassName="toolbarClassName"
        wrapperClassName="wrapperClassName"
        editorClassName="editorClassName"
        onEditorStateChange={handleEditorStateChange}
        handlePastedText={handlePastedText}
        handleKeyCommand={handleKeyCommand}
        stripPastedStyles={false}
        readOnly={props.readOnly}
        onFocus={() => setState(prev => ({ ...prev, isFocused: true }))}
        onBlur={() => setState(prev => ({ ...prev, isFocused: false }))}
        handleBeforeInput={handleBeforeInput}
      />
      <div className="bottomContainer">
        <div className="WysiwygEditorError" id={props.errorId} role="alert">
          {props.error}
        </div>
        <div className="counter">
          {state.counter}/{props.size}
        </div>
      </div>
    </section>
  );
};

export default WysiwygEditor;
